import { CommonModule } from '@angular/common'
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { TranslateModule } from '@ngx-translate/core'

import { AvatarComponent } from '../../../../avatar/avatar.component'
import { EditorThread, EditorThreadComment } from '../../editor-thread.types'
import { formatRelativeTime } from '../../utils/format-relative-time'

export type ThreadCommentsCurrentUser = {
  name: string
}

type CommentViewModel = EditorThreadComment & {
  createdAtRelative: string | null
}

@Component({
  selector: 'app-thread-comments',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, AvatarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './thread-comments.component.html',
  styleUrl: './thread-comments.component.scss',
})
export class ThreadCommentsComponent implements OnChanges, AfterViewChecked {
  @Input()
  thread: EditorThread | null = null

  @Input({ required: true })
  currentUser!: ThreadCommentsCurrentUser

  @Input()
  canPostComment = true

  @Output()
  submitComment = new EventEmitter<string>()

  @ViewChild('commentList')
  commentListRef: ElementRef<HTMLDivElement> | null = null

  draft = ''
  comments: CommentViewModel[] = []

  private changeDetectorRef = inject(ChangeDetectorRef)
  private shouldScrollToBottom = false
  private lastSeenCommentId: string | null = null

  get commentCount(): number {
    return this.comments.length
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!('thread' in changes)) {
      return
    }
    const raw = this.thread?.comments ?? []
    this.comments = raw.map((comment) => ({ ...comment, createdAtRelative: formatRelativeTime(comment.createdAt) }))

    const latestId = this.comments.at(-1)?.id ?? null
    if (latestId !== null && latestId !== this.lastSeenCommentId) {
      this.lastSeenCommentId = latestId
      this.shouldScrollToBottom = true
    }
  }

  ngAfterViewChecked() {
    if (!this.shouldScrollToBottom) {
      return
    }
    const element = this.commentListRef?.nativeElement ?? null
    if (element !== null) {
      element.scrollTop = element.scrollHeight
      this.shouldScrollToBottom = false
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey) {
      return
    }
    event.preventDefault()
    this.submit()
  }

  submit() {
    const trimmed = this.draft.trim()
    if (trimmed.length === 0 || !this.canPostComment) {
      return
    }
    this.draft = ''
    this.submitComment.emit(trimmed)
    this.changeDetectorRef.markForCheck()
  }

  cancel() {
    this.draft = ''
    this.changeDetectorRef.markForCheck()
  }
}
